import { type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { calculateTimeUnits } from "src/utils/time/calculateTimeUnits";
import { TIME_WINDOW_ALL_TIME } from "../../../../TimeWindow.constants";
import { durationMinutesToToken } from "../../../../TimeWindow.selection";
import { TIME_UNIT_CONFIG } from "./TimeWindowPresetOption.constants";
import type { TimeUnitConfig } from "./TimeWindowPresetOption.types";

export const useTimeAdjustment = (
  t: number,
  setTimeWindow: (value: string) => void
) => {
  const [localTimeWindow, setLocalTimeWindow] = useState<number>(t);

  const { hours, days, months, years } = useMemo(
    () => calculateTimeUnits(localTimeWindow)!,
    [localTimeWindow]
  );

  const timeUnitConfig = useMemo(() => {
    // Find the first matching time unit configuration
    const config = TIME_UNIT_CONFIG.find((c) => c.check(years, months, days, hours));

    // Default to last config if no configuration matches
    return config || TIME_UNIT_CONFIG.at(-1);
  }, [years, months, days, hours]) as TimeUnitConfig;

  const handleAdjustTime = useCallback(
    (direction: "up" | "down") => (e: MouseEvent) => {
      e.stopPropagation();

      const { increment, minimum, maximum = Infinity } = timeUnitConfig;

      if (direction === "up") {
        const newValue = localTimeWindow + increment;
        // Past ~10 years everything reads as "All time", so further increments
        // would look like a dead button while still needing as many clicks back.
        const isRedundantAllTimeStep =
          durationMinutesToToken(newValue) === TIME_WINDOW_ALL_TIME &&
          durationMinutesToToken(localTimeWindow) === TIME_WINDOW_ALL_TIME;

        if (newValue <= maximum && !isRedundantAllTimeStep) {
          setLocalTimeWindow(newValue);
        }
      } else {
        const newValue = localTimeWindow - increment;
        if (newValue >= minimum) {
          setLocalTimeWindow(newValue);
        }
      }
    },
    [localTimeWindow, timeUnitConfig]
  );

  const handleSelect = useCallback(() => {
    setTimeWindow(durationMinutesToToken(localTimeWindow));
  }, [localTimeWindow, setTimeWindow]);

  useEffect(() => {
    setLocalTimeWindow(t);
  }, [t]);

  return {
    localTime: localTimeWindow,
    handleAdjustTime,
    handleSelect,
  };
};
